import { useJobLedger, LedgerEntry } from '@/hooks/useJobLedger';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, AlertTriangle, ChevronDown, Loader2, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface JobLedgerViewProps {
  jobId: string;
  defaultOpen?: boolean;
}

const ACCOUNT_COLORS: Record<string, string> = {
  client: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  escrow: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  platform: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  vendor: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  tax: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  cc_fees: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
  settlement: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
};

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
}

function AccountBadge({ account }: { account: string }) {
  return (
    <Badge variant="secondary" className={cn('capitalize', ACCOUNT_COLORS[account])}>
      {account}
    </Badge>
  );
}

function BalanceCard({ 
  label, 
  cents, 
  account,
  subtitle 
}: { 
  label: string; 
  cents: number; 
  account: string;
  subtitle?: string;
}) {
  const isPositive = cents > 0;
  const isNegative = cents < 0;
  const isZero = cents === 0;

  return (
    <div className={cn(
      'p-3 rounded-lg border',
      isZero && 'bg-muted/50',
      isPositive && 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800',
      isNegative && 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
    )}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground uppercase">{label}</span>
        <AccountBadge account={account} />
      </div>
      <p className={cn(
        'text-lg font-bold',
        isZero && 'text-muted-foreground',
        isPositive && 'text-green-700 dark:text-green-400',
        isNegative && 'text-red-700 dark:text-red-400'
      )}>
        {isNegative ? '-' : ''}${formatCents(Math.abs(cents))}
      </p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

function LedgerEntriesTable({ entries }: { entries: LedgerEntry[] }) {
  // Group entries by correlation_id for visual grouping
  let currentCorrelationId: string | null = null;
  let rowIndex = 0;

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Date</TableHead>
            <TableHead className="w-[120px]">Type</TableHead>
            <TableHead className="w-[100px]">Account</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-[80px]">Status</TableHead>
            <TableHead className="text-right w-[120px]">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => {
            const isNewGroup = entry.correlation_id !== currentCorrelationId;
            if (isNewGroup) {
              currentCorrelationId = entry.correlation_id;
              rowIndex++;
            }
            const isEvenGroup = rowIndex % 2 === 0;

            return (
              <TableRow 
                key={entry.id} 
                className={cn(
                  isEvenGroup && 'bg-muted/30'
                )}
              >
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(entry.created_at), 'M/d HH:mm')}
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {entry.entry_type}
                </TableCell>
                <TableCell>
                  <AccountBadge account={entry.account} />
                </TableCell>
                <TableCell className="text-sm">
                  {entry.description || '-'}
                </TableCell>
                <TableCell>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      'text-xs',
                      entry.status === 'confirmed' && 'bg-green-50 dark:bg-green-950/20',
                      entry.status === 'pending' && 'bg-yellow-50 dark:bg-yellow-950/20',
                      entry.status === 'reversed' && 'bg-red-50 dark:bg-red-950/20'
                    )}
                  >
                    {entry.status}
                  </Badge>
                </TableCell>
                <TableCell className={cn(
                  'text-right font-mono text-sm font-medium',
                  entry.amount_cents > 0 && 'text-green-700 dark:text-green-400',
                  entry.amount_cents < 0 && 'text-red-700 dark:text-red-400'
                )}>
                  {entry.amount_cents > 0 ? '+' : ''}{formatCents(entry.amount_cents)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function formatCentsDisplay(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { 
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
}

export function JobLedgerView({ jobId, defaultOpen = false }: JobLedgerViewProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { entries, balance, reconciliation, isLoading, error } = useJobLedger(jobId);

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 text-destructive rounded-lg text-sm">
        Failed to load ledger data: {error.message}
      </div>
    );
  }

  const confirmedEntries = entries.filter(e => e.status === 'confirmed');
  
  // Determine true reconciliation status
  const hasPendingRefund = (reconciliation?.pending_refund_cents ?? 0) > 0;
  const hasPendingCharge = (reconciliation?.pending_charge_cents ?? 0) > 0;
  const hasPendingActions = hasPendingRefund || hasPendingCharge;
  const isFullyReconciled = balance?.is_balanced && 
    !hasPendingActions && 
    reconciliation?.reconciliation_status === 'completed';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
            Job Ledger & Balance Validation
          </span>
          {!isLoading && balance && (
            <Badge 
              variant="outline" 
              className={cn(
                isFullyReconciled 
                  ? 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                  : hasPendingActions && balance.is_balanced
                    ? 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                    : !balance.is_balanced
                      ? 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
                      : 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
              )}
            >
              {isFullyReconciled ? (
                <><CheckCircle className="h-3 w-3 mr-1" /> Reconciled</>
              ) : hasPendingActions && balance.is_balanced ? (
                <><Clock className="h-3 w-3 mr-1" /> Action Required</>
              ) : !balance.is_balanced ? (
                <><AlertTriangle className="h-3 w-3 mr-1" /> Imbalanced</>
              ) : (
                <><CheckCircle className="h-3 w-3 mr-1" /> Balanced</>
              )}
            </Badge>
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Reconciliation Status Banner */}
              {balance && (
                <div className={cn(
                  'p-4 rounded-lg border flex items-start gap-3',
                  isFullyReconciled 
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : hasPendingActions && balance.is_balanced
                      ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                      : !balance.is_balanced
                        ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                        : 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                )}>
                  {isFullyReconciled ? (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  ) : hasPendingActions && balance.is_balanced ? (
                    <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  ) : !balance.is_balanced ? (
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  )}
                  <div>
                    <p className={cn(
                      'font-medium',
                      isFullyReconciled 
                        ? 'text-green-800 dark:text-green-300'
                        : hasPendingActions && balance.is_balanced
                          ? 'text-amber-800 dark:text-amber-300'
                          : !balance.is_balanced
                            ? 'text-red-800 dark:text-red-300'
                            : 'text-green-800 dark:text-green-300'
                    )}>
                      {isFullyReconciled 
                        ? 'Fully Reconciled' 
                        : hasPendingActions && balance.is_balanced
                          ? 'Pending Financial Actions'
                          : !balance.is_balanced
                            ? `Ledger Imbalanced: $${formatCents(Math.abs(balance.total_balance_cents))}`
                            : 'Ledger Balanced'}
                    </p>
                    <p className={cn(
                      'text-sm',
                      isFullyReconciled 
                        ? 'text-green-700 dark:text-green-400'
                        : hasPendingActions && balance.is_balanced
                          ? 'text-amber-700 dark:text-amber-400'
                          : !balance.is_balanced
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-green-700 dark:text-green-400'
                    )}>
                      {isFullyReconciled 
                        ? 'Ledger balanced and all financial actions completed'
                        : hasPendingActions && balance.is_balanced
                          ? 'Ledger is balanced, but approvals are pending'
                          : !balance.is_balanced
                            ? 'Accounts do not sum to zero - review entries before proceeding'
                            : 'All accounts sum to zero'}
                    </p>
                    {hasPendingActions && balance.is_balanced && (
                      <ul className="mt-2 text-sm text-amber-700 dark:text-amber-400 list-disc list-inside">
                        {hasPendingRefund && (
                          <li>Pending client refund: {formatCentsDisplay(reconciliation!.pending_refund_cents!)} awaiting approval</li>
                        )}
                        {hasPendingCharge && (
                          <li>Pending client charge: {formatCentsDisplay(reconciliation!.pending_charge_cents!)} requires action</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* Account Balances Grid */}
              {balance && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Account Balances</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    <BalanceCard 
                      label="Client" 
                      cents={balance.client_balance_cents} 
                      account="client"
                      subtitle="(charged)"
                    />
                    <BalanceCard 
                      label="Escrow" 
                      cents={balance.escrow_balance_cents} 
                      account="escrow"
                      subtitle="(held)"
                    />
                    <BalanceCard 
                      label="Platform" 
                      cents={balance.platform_earned_cents} 
                      account="platform"
                      subtitle="(earned)"
                    />
                    <BalanceCard 
                      label="Vendor" 
                      cents={balance.vendor_balance_cents} 
                      account="vendor"
                      subtitle="(owed)"
                    />
                    <BalanceCard 
                      label="Tax" 
                      cents={balance.tax_balance_cents} 
                      account="tax"
                      subtitle="(collected)"
                    />
                    <BalanceCard 
                      label="CC Fees" 
                      cents={balance.cc_fees_balance_cents} 
                      account="cc_fees"
                      subtitle="(processing fees)"
                    />
                    <BalanceCard 
                      label="Settlement" 
                      cents={balance.settlement_balance_cents} 
                      account="settlement"
                      subtitle="(cash movements)"
                    />
                  </div>
                </div>
              )}

              {/* Ledger Entries Table */}
              {confirmedEntries.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Ledger Entries ({confirmedEntries.length} confirmed)
                  </h4>
                  <LedgerEntriesTable entries={confirmedEntries} />
                </div>
              )}

              {entries.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No ledger entries found for this job.
                </p>
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
